import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import {
  PerformanceChartResolution,
  PerformanceReturnMethod,
} from "@repo/types";
import { queryOnce } from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import {
  DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  queryKeys,
  withSessionQueryScope,
} from "~/lib/query-keys";
import { queryClient } from "~/lib/query-client";
import { type PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import {
  getBrowserPlatformApiUnbatchedClient,
  resetBrowserPlatformApiClient,
} from "~/lib/trpc/browser-client";
import {
  type PortfolioInstanceModel,
  normalizePortfolioAddress,
} from "./models";
import { type PortfolioDetailNormalizedResponse } from "./normalizers";

import {
  applyDashboardPayloadToCollections,
  applyDetailPayloadToCollections,
  applyPerformanceSeriesPayloadToCollections,
  applyPortfolioScheduledFunctionsPayloadToCollections,
  ASSET_BATCH_LIMIT,
  buildPortfolioDetailViewQuery,
  canReadCollectionRows,
  DASHBOARD_SEED_INCLUDE_CACHE_KEY,
  DASHBOARD_SEED_OWNER_SCOPE_CACHE_ID,
  DASHBOARD_SEED_STALE_WHILE_REVALIDATE_TTL_MS,
  chunkArray,
  createPortfolioDomainCollectionDefinitions,
  deleteStalePortfolioBoundSummaryRows,
  extractPortfolioIdsFromPayload,
  emptyPortfolioPerformanceSeriesResponse,
  invalidatePortfolioDomainCollectionQueries,
  invalidatePortfolioDomainDetailQueries,
  matchesQueryKeyPrefix,
  mergeNormalizedPayloads,
  normalizePositiveInteger,
  normalizePortfolioNormalizedContractError,
  PORTFOLIO_DETAIL_BOOTSTRAP_STALE_TIME_MS,
  PORTFOLIO_DETAIL_PORTFOLIO_BOUND_QUERY_KEYS,
  PORTFOLIO_SUMMARY_COLLECTION_QUERY_KEYS,
  queryDashboardNormalizedData,
  queryPortfolioDetailNormalizedData,
  queryPortfolioPerformanceSeriesData,
  queryPortfolioScheduledFunctionsData,
  readCollectionRows,
  STRATEGY_BATCH_LIMIT,
  toBrowserCollectionCacheKey,
  uniqueNonEmpty,
  uniqueNormalizedAddresses,
  type DashboardNormalizedPayload,
  type PortfolioDbDependencies,
  type PortfolioDetailNormalizedInput,
  type PortfolioDetailBootstrapInput,
  type PortfolioDomainCollections,
  type PortfolioSummaryHydrationInclude,
} from "./collection-defs";
import {
  portfolioGraphStore,
  type PortfolioGraphCachedDashboardSeed,
  type PortfolioGraphContext,
  type PortfolioGraphScope,
  type PortfolioGraphSummaryComponent,
  type PortfolioGraphSummaryComponentStatus,
} from "./portfolio-graph-store";

interface BrowserPortfolioCollectionOptions {
  ownerAddresses?: string[];
  sessionScopeToken?: string;
}

type CachedPortfolioDashboardSeed = PortfolioGraphCachedDashboardSeed;
type PortfolioSummaryHydrationComponent = PortfolioGraphSummaryComponent;
type HydratedPortfolioSummaryComponentStatus =
  PortfolioGraphSummaryComponentStatus;

export interface PortfolioSummaryHydrationOptions {
  includeAssetPrices?: boolean;
  includeAssets?: boolean;
  includeDashboardAssetSummaries?: boolean;
  includePerformance?: boolean;
  performanceMaxPointsPerPortfolio?: number;
  performanceResolution?: PerformanceChartResolution;
  includePortfolioInstances?: boolean;
  includePositions?: boolean;
  ownerAddresses?: string[];
  sessionScopeToken?: string;
  includeVaults?: boolean;
}

function toPortfolioGraphScope(
  input: BrowserPortfolioCollectionOptions = {},
): Required<PortfolioGraphScope> {
  const ownerAddresses = uniqueNormalizedAddresses(input.ownerAddresses ?? []);
  const sessionScopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  return {
    cacheKey: toBrowserCollectionCacheKey({
      ownerAddresses,
      sessionScopeToken,
    }),
    sessionScopeToken,
  };
}

function readExistingPortfolioGraph<Result>(
  input: BrowserPortfolioCollectionOptions,
  read: (context: PortfolioGraphContext | undefined) => Result,
): Result {
  return portfolioGraphStore.invalidate(toPortfolioGraphScope(input), {
    invalidate: ([context]) => read(context),
    kind: "custom",
  });
}

function clearPortfolioSummaryHydrationForSession(
  sessionScopeToken?: string,
): void {
  const scopeToken = sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;

  portfolioGraphStore.invalidate(
    { sessionScopeToken: scopeToken },
    {
      invalidate: (contexts) => {
        for (const context of contexts) {
          context.hydration.summaries.clear();
          context.hydration.summaryComponents.clear();
          context.hydration.summaryHydrations.clear();
        }
      },
      kind: "custom",
    },
  );
}

function clearPortfolioSummaryHydrationForPortfolio(input: {
  portfolioId: string;
  sessionScopeToken?: string;
}): void {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return;
  }

  const scopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;

  portfolioGraphStore.invalidate(
    { sessionScopeToken: scopeToken },
    {
      invalidate: (contexts) => {
        for (const context of contexts) {
          context.hydration.summaries.delete(normalizedPortfolioId);
          context.hydration.summaryComponents.delete(normalizedPortfolioId);
          context.hydration.summaryHydrations.clear();
        }
      },
      kind: "custom",
    },
  );
}

function toHydrationIdsCacheKey(values: string[]): string {
  const normalizedValues = uniqueNonEmpty(values);
  if (normalizedValues.length === 0) {
    return "__ids:none__";
  }

  return [...normalizedValues]
    .sort((left, right) => left.localeCompare(right))
    .join("|");
}

function toPortfolioIndexHydrationCacheKey(input: {
  ownerAddresses: string[];
  sessionScopeToken?: string;
}): string {
  const normalizedOwnerAddresses = uniqueNormalizedAddresses(
    input.ownerAddresses,
  );
  return `index::${toBrowserCollectionCacheKey({
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  })}`;
}

function toPortfolioSummaryHydrationCacheKey(input: {
  include: {
    assetPrices: boolean;
    assets: boolean;
    dashboardAssetSummaries: boolean;
    performance: boolean;
    performanceMaxPointsPerPortfolio?: number;
    performanceResolution?: PerformanceChartResolution;
    portfolioInstances: boolean;
    positions: boolean;
    vaults: boolean;
  };
  ownerAddresses?: string[];
  sessionScopeToken?: string;
  strategyInstanceIds: string[];
}): string {
  const normalizedOwnerAddresses = uniqueNormalizedAddresses(
    input.ownerAddresses ?? [],
  );

  return [
    "summaries",
    toBrowserCollectionCacheKey({
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: input.sessionScopeToken,
    }),
    toHydrationIdsCacheKey(input.strategyInstanceIds),
    `assetPrices:${input.include.assetPrices}`,
    `assets:${input.include.assets}`,
    `dashboardAssetSummaries:${input.include.dashboardAssetSummaries}`,
    `performance:${input.include.performance}`,
    `performanceResolution:${input.include.performanceResolution ?? "default"}`,
    `performanceMaxPoints:${input.include.performanceMaxPointsPerPortfolio ?? "default"}`,
    `portfolioInstances:${input.include.portfolioInstances}`,
    `positions:${input.include.positions}`,
    `vaults:${input.include.vaults}`,
  ].join("::");
}

function toPortfolioDashboardSeedHydrationCacheKey(input: {
  ownerAddresses?: string[];
  sessionScopeToken?: string;
}): string {
  return toPortfolioSummaryHydrationCacheKey({
    include: DASHBOARD_SEED_INCLUDE_CACHE_KEY,
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
    strategyInstanceIds: [DASHBOARD_SEED_OWNER_SCOPE_CACHE_ID],
  });
}

function getInFlightPortfolioDashboardSeedPromise(input: {
  ownerAddresses?: string[];
  sessionScopeToken?: string;
}): Promise<void> | undefined {
  const hydrationCacheKey = toPortfolioDashboardSeedHydrationCacheKey(input);
  return readExistingPortfolioGraph(input, (context) =>
    context?.hydration.summaryHydrations.get(hydrationCacheKey),
  );
}

function readPortfolioDashboardSeedCache(
  hydrationCacheKey: string,
  options: BrowserPortfolioCollectionOptions,
): CachedPortfolioDashboardSeed | null {
  const cachedSeed = readExistingPortfolioGraph(options, (context) =>
    context?.hydration.dashboardSeeds.get(hydrationCacheKey),
  );
  if (!cachedSeed) {
    return null;
  }

  if (
    Date.now() - cachedSeed.cachedAtMs >
    DASHBOARD_SEED_STALE_WHILE_REVALIDATE_TTL_MS
  ) {
    readExistingPortfolioGraph(options, (context) =>
      context?.hydration.dashboardSeeds.delete(hydrationCacheKey),
    );
    return null;
  }

  return cachedSeed;
}

function writePortfolioDashboardSeedCache(
  hydrationCacheKey: string,
  payload: DashboardNormalizedPayload,
  strategyInstanceIds: string[],
  options: BrowserPortfolioCollectionOptions,
) {
  readExistingPortfolioGraph(options, (context) => {
    context?.hydration.dashboardSeeds.set(hydrationCacheKey, {
      cachedAtMs: Date.now(),
      payload,
      strategyInstanceIds,
    });
  });
}

function applyPortfolioDashboardSeedPayload(input: {
  deleteStalePortfolioRows?: boolean;
  ownerAddresses: string[];
  payload: DashboardNormalizedPayload;
  sessionScopeToken?: string;
}): string[] {
  const collections = getPortfolioDomainCollections({
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });
  const principalOwnerAddressSet = new Set(input.ownerAddresses);
  const strategyInstanceIds = extractPortfolioIdsFromPayload(input.payload);

  if (input.deleteStalePortfolioRows ?? true) {
    deleteStalePortfolioBoundSummaryRows({
      collections,
      include: {
        dashboardAssetSummaries: true,
        positions: true,
        vaults: true,
      },
      payload: input.payload,
      portfolioIds: new Set(strategyInstanceIds),
    });
  }
  applyDashboardPayloadToCollections(
    collections,
    input.payload,
    principalOwnerAddressSet,
  );
  markOwnerPortfolioListHydrated(strategyInstanceIds, {
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });
  markPortfolioSummariesHydrated(strategyInstanceIds, {
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });
  markPortfolioSummaryComponentsHydrated(
    strategyInstanceIds,
    ["assetPrices", "assets", "dashboardAssetSummaries", "positions", "vaults"],
    {
      ownerAddresses: input.ownerAddresses,
      sessionScopeToken: input.sessionScopeToken,
    },
  );

  return strategyInstanceIds;
}

function markPortfolioSummariesHydrated(
  strategyInstanceIds: string[],
  options?: BrowserPortfolioCollectionOptions,
) {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0) {
    return;
  }

  readExistingPortfolioGraph(options ?? {}, (context) => {
    if (!context) {
      return;
    }
    for (const strategyInstanceId of normalizedStrategyInstanceIds) {
      context.hydration.summaries.add(strategyInstanceId);
    }
  });
}

function markOwnerPortfolioListHydrated(
  strategyInstanceIds: string[],
  options?: BrowserPortfolioCollectionOptions,
) {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0) {
    return;
  }

  readExistingPortfolioGraph(options ?? {}, (context) => {
    if (!context) {
      return;
    }
    context.hydration.ownerPortfolioIds.clear();
    for (const strategyInstanceId of normalizedStrategyInstanceIds) {
      context.hydration.ownerPortfolioIds.add(strategyInstanceId);
    }
  });
}

function haveHydratedOwnerPortfolioListRows(
  portfolioRows: PortfolioInstanceModel[],
  options?: BrowserPortfolioCollectionOptions,
): boolean {
  const ownerPortfolioIds = readExistingPortfolioGraph(
    options ?? {},
    (context) => context?.hydration.ownerPortfolioIds,
  );
  if (!ownerPortfolioIds || ownerPortfolioIds.size === 0) {
    return false;
  }

  const rowIds = new Set(portfolioRows.map((portfolio) => portfolio.id));
  return Array.from(ownerPortfolioIds).every((portfolioId) =>
    rowIds.has(portfolioId),
  );
}

function createPortfolioSummaryComponentStatus(
  existing?: HydratedPortfolioSummaryComponentStatus,
): HydratedPortfolioSummaryComponentStatus {
  return {
    assetIds: new Set(existing?.assetIds),
    assetPriceAssetIds: new Set(existing?.assetPriceAssetIds),
    components: new Set(existing?.components),
    dashboardAssetSummaryIds: new Set(existing?.dashboardAssetSummaryIds),
    positionIds: new Set(existing?.positionIds),
    vaultIds: new Set(existing?.vaultIds),
  };
}

function capturePortfolioSummaryComponentStatus(
  input: {
    components: PortfolioSummaryHydrationComponent[];
    collections: PortfolioDomainCollections;
    portfolioId: string;
  },
  existing?: HydratedPortfolioSummaryComponentStatus,
): HydratedPortfolioSummaryComponentStatus {
  const status = createPortfolioSummaryComponentStatus(existing);
  const componentSet = new Set(input.components);
  const positions = readCollectionRows(input.collections.positions).filter(
    (position) => position.portfolioId === input.portfolioId,
  );
  const vaults = readCollectionRows(input.collections.vaults).filter(
    (vault) => vault.portfolioId === input.portfolioId,
  );
  const dashboardAssetSummaries = readCollectionRows(
    input.collections.dashboardAssetSummaries,
  ).filter((summary) => summary.portfolioId === input.portfolioId);
  const positionAssetIds = new Set(
    positions.map((position) => position.assetId),
  );

  if (componentSet.has("dashboardAssetSummaries")) {
    status.dashboardAssetSummaryIds = new Set(
      dashboardAssetSummaries.map((summary) => summary.id),
    );
  }
  if (componentSet.has("positions")) {
    status.positionIds = new Set(positions.map((position) => position.id));
  }
  if (componentSet.has("vaults")) {
    status.vaultIds = new Set(vaults.map((vault) => vault.id));
  }
  if (componentSet.has("assets")) {
    const assetIds = new Set<string>();
    for (const asset of readCollectionRows(input.collections.assets)) {
      if (positionAssetIds.has(asset.id)) {
        assetIds.add(asset.id);
      }
    }
    status.assetIds = assetIds;
  }
  if (componentSet.has("assetPrices")) {
    const assetPriceAssetIds = new Set<string>();
    for (const assetPrice of readCollectionRows(
      input.collections.assetPrices,
    )) {
      if (positionAssetIds.has(assetPrice.assetId)) {
        assetPriceAssetIds.add(assetPrice.assetId);
      }
    }
    status.assetPriceAssetIds = assetPriceAssetIds;
  }

  return status;
}

function hasExpectedRows(
  actualIds: ReadonlySet<string>,
  expectedIds: ReadonlySet<string>,
): boolean {
  for (const expectedId of expectedIds) {
    if (!actualIds.has(expectedId)) {
      return false;
    }
  }

  return true;
}

function havePortfolioSummaryComponentRows(input: {
  collections: PortfolioDomainCollections;
  components: PortfolioSummaryHydrationComponent[];
  portfolioId: string;
  status: HydratedPortfolioSummaryComponentStatus;
}): boolean {
  const componentSet = new Set(input.components);

  if (componentSet.has("dashboardAssetSummaries")) {
    const currentDashboardAssetSummaryIds = new Set<string>();
    for (const summary of readCollectionRows(
      input.collections.dashboardAssetSummaries,
    )) {
      if (summary.portfolioId === input.portfolioId) {
        currentDashboardAssetSummaryIds.add(summary.id);
      }
    }
    if (
      !hasExpectedRows(
        currentDashboardAssetSummaryIds,
        input.status.dashboardAssetSummaryIds,
      )
    ) {
      return false;
    }
  }

  if (componentSet.has("positions")) {
    const currentPositionIds = new Set<string>();
    for (const position of readCollectionRows(input.collections.positions)) {
      if (position.portfolioId === input.portfolioId) {
        currentPositionIds.add(position.id);
      }
    }
    if (!hasExpectedRows(currentPositionIds, input.status.positionIds)) {
      return false;
    }
  }

  if (componentSet.has("vaults")) {
    const currentVaultIds = new Set<string>();
    for (const vault of readCollectionRows(input.collections.vaults)) {
      if (vault.portfolioId === input.portfolioId) {
        currentVaultIds.add(vault.id);
      }
    }
    if (!hasExpectedRows(currentVaultIds, input.status.vaultIds)) {
      return false;
    }
  }

  if (componentSet.has("assets")) {
    const currentAssetIds = new Set(
      readCollectionRows(input.collections.assets).map((asset) => asset.id),
    );
    if (!hasExpectedRows(currentAssetIds, input.status.assetIds)) {
      return false;
    }
  }

  if (componentSet.has("assetPrices")) {
    const currentAssetPriceAssetIds = new Set(
      readCollectionRows(input.collections.assetPrices).map(
        (assetPrice) => assetPrice.assetId,
      ),
    );
    if (
      !hasExpectedRows(
        currentAssetPriceAssetIds,
        input.status.assetPriceAssetIds,
      )
    ) {
      return false;
    }
  }

  return true;
}

function markPortfolioSummaryComponentsHydrated(
  strategyInstanceIds: string[],
  components: PortfolioSummaryHydrationComponent[],
  options?: BrowserPortfolioCollectionOptions,
): void {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0 || components.length === 0) {
    return;
  }

  readExistingPortfolioGraph(options ?? {}, (context) => {
    if (!context) {
      return;
    }
    for (const strategyInstanceId of normalizedStrategyInstanceIds) {
      const hydratedStatus = capturePortfolioSummaryComponentStatus(
        {
          components,
          collections: context.collections,
          portfolioId: strategyInstanceId,
        },
        context.hydration.summaryComponents.get(strategyInstanceId),
      );

      for (const component of components) {
        hydratedStatus.components.add(component);
      }

      context.hydration.summaryComponents.set(
        strategyInstanceId,
        hydratedStatus,
      );
    }
  });
}

function normalizePortfolioSummaryHydrationInclude(
  options?: PortfolioSummaryHydrationOptions,
): PortfolioSummaryHydrationInclude {
  return {
    assetPrices: options?.includeAssetPrices ?? true,
    assets: options?.includeAssets ?? true,
    dashboardAssetSummaries: options?.includeDashboardAssetSummaries ?? false,
    performance: options?.includePerformance ?? false,
    performanceMaxPointsPerPortfolio: options?.performanceMaxPointsPerPortfolio,
    performanceResolution: options?.performanceResolution,
    portfolioInstances: options?.includePortfolioInstances ?? true,
    positions: options?.includePositions ?? true,
    vaults: options?.includeVaults ?? true,
  };
}

function getPortfolioSummaryHydrationComponents(
  include: PortfolioSummaryHydrationInclude,
): PortfolioSummaryHydrationComponent[] {
  const components: PortfolioSummaryHydrationComponent[] = [];
  if (include.assetPrices) {
    components.push("assetPrices");
  }
  if (include.assets) {
    components.push("assets");
  }
  if (include.dashboardAssetSummaries) {
    components.push("dashboardAssetSummaries");
  }
  if (include.positions) {
    components.push("positions");
  }
  if (include.vaults) {
    components.push("vaults");
  }

  return components;
}

function havePortfolioSummaryComponentsHydrated(
  strategyInstanceIds: string[],
  components: PortfolioSummaryHydrationComponent[],
  options?: BrowserPortfolioCollectionOptions,
): boolean {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0 || components.length === 0) {
    return false;
  }

  const context = readExistingPortfolioGraph(options ?? {}, (value) => value);
  if (!context) {
    return false;
  }

  return normalizedStrategyInstanceIds.every((strategyInstanceId) => {
    const hydratedStatus =
      context.hydration.summaryComponents.get(strategyInstanceId);
    if (!hydratedStatus) {
      return false;
    }

    const hasComponentMarkers = components.every((component) =>
      hydratedStatus.components.has(component),
    );
    if (!hasComponentMarkers) {
      return false;
    }

    return havePortfolioSummaryComponentRows({
      collections: context.collections,
      components,
      portfolioId: strategyInstanceId,
      status: hydratedStatus,
    });
  });
}

export function hasPortfolioSummariesHydrated(
  strategyInstanceIds: string[],
  options?: PortfolioSummaryHydrationOptions,
): boolean {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0) {
    return true;
  }

  const include = normalizePortfolioSummaryHydrationInclude(options);
  if (
    !include.assetPrices &&
    !include.assets &&
    !include.dashboardAssetSummaries &&
    !include.performance &&
    !include.portfolioInstances &&
    !include.positions &&
    !include.vaults
  ) {
    return true;
  }
  if (include.performance) {
    return false;
  }

  const context = readExistingPortfolioGraph(options ?? {}, (value) => value);
  if (!context) {
    return false;
  }

  if (include.portfolioInstances) {
    const hydratedSummaryIds = context.hydration.summaries;
    if (hydratedSummaryIds.size === 0) {
      return false;
    }

    const portfolioRows = canReadCollectionRows<PortfolioInstanceModel>(
      context.collections.portfolioInstances,
    )
      ? readCollectionRows(context.collections.portfolioInstances)
      : [];
    const portfolioIds = new Set(
      portfolioRows.map((portfolio) => portfolio.id),
    );
    const hasPortfolioRows = normalizedStrategyInstanceIds.every(
      (strategyInstanceId) =>
        portfolioIds.has(strategyInstanceId) &&
        hydratedSummaryIds.has(strategyInstanceId),
    );
    if (!hasPortfolioRows) {
      return false;
    }
  }

  const components = getPortfolioSummaryHydrationComponents(include);
  return (
    components.length === 0 ||
    havePortfolioSummaryComponentsHydrated(
      normalizedStrategyInstanceIds,
      components,
      options,
    )
  );
}

export async function ensurePortfolioSummariesHydrated(
  strategyInstanceIds: string[],
  options?: PortfolioSummaryHydrationOptions,
): Promise<void> {
  const missingStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds).filter(
    (strategyInstanceId) =>
      !hasPortfolioSummariesHydrated([strategyInstanceId], options),
  );
  if (missingStrategyInstanceIds.length === 0) {
    return;
  }

  await hydratePortfolioSummaries(missingStrategyInstanceIds, options);
}

function markPortfolioDetailsHydrated(
  input: {
    hasPerformanceSeriesRows: boolean;
    portfolioId: string;
    positionsInSharesUiEnabled: boolean;
  },
  options?: BrowserPortfolioCollectionOptions,
) {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return;
  }

  readExistingPortfolioGraph(options ?? {}, (context) => {
    context?.hydration.details.set(normalizedPortfolioId, {
      hasPerformanceSeriesRows: input.hasPerformanceSeriesRows,
      positionsInSharesUiEnabled: input.positionsInSharesUiEnabled,
    });
  });
}

function getPortfolioDependencies(
  options?: BrowserPortfolioCollectionOptions,
): PortfolioDbDependencies {
  const ownerAddresses = uniqueNormalizedAddresses(
    options?.ownerAddresses ?? [],
  );
  return {
    ownerAddresses,
    platformApi: getBrowserPlatformApiUnbatchedClient(),
    queryClient: queryClient as unknown as QueryClient,
    sessionScopeToken:
      options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  };
}

export function getPortfolioDomainCollections(
  options?: BrowserPortfolioCollectionOptions,
): PortfolioDomainCollections {
  const normalizedOwnerAddresses = uniqueNormalizedAddresses(
    options?.ownerAddresses ?? [],
  );
  const normalizedOptions = {
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  };
  const scope = toPortfolioGraphScope(normalizedOptions);

  return portfolioGraphStore.ensureHydrated(scope, {
    createCollections: () =>
      createPortfolioDomainCollections(
        getPortfolioDependencies(normalizedOptions),
      ),
    kind: "read",
    read: (context) => context.collections,
  });
}

export function createPortfolioDomainCollections(
  dependencies: PortfolioDbDependencies,
): PortfolioDomainCollections {
  return createPortfolioDomainCollectionDefinitions(dependencies, {
    getInFlightPortfolioDashboardSeedPromise,
    haveHydratedOwnerPortfolioListRows,
    havePortfolioSummaryComponentsHydrated,
  });
}

export function subscribeToPortfolioDomainCollections(
  options?: BrowserPortfolioCollectionOptions,
): () => void {
  const normalizedOptions = {
    ownerAddresses: uniqueNormalizedAddresses(options?.ownerAddresses ?? []),
    sessionScopeToken: options?.sessionScopeToken,
  };
  return portfolioGraphStore.ensureHydrated(
    toPortfolioGraphScope(normalizedOptions),
    {
      createCollections: () =>
        createPortfolioDomainCollections(
          getPortfolioDependencies(normalizedOptions),
        ),
      kind: "subscribe",
    },
  );
}

export function getPortfolioDomainSeedSnapshot(
  options?: BrowserPortfolioCollectionOptions,
): {
  hasHydratedSummaries: boolean;
  hasPortfolioInstances: boolean;
  hydratedStrategyInstanceIds: string[];
  strategyInstanceIds: string[];
} {
  const context = readExistingPortfolioGraph(options ?? {}, (value) => value);
  if (!context) {
    return {
      hasHydratedSummaries: false,
      hasPortfolioInstances: false,
      hydratedStrategyInstanceIds: [],
      strategyInstanceIds: [],
    };
  }

  const portfolioInstanceRows = canReadCollectionRows<PortfolioInstanceModel>(
    context.collections.portfolioInstances,
  )
    ? readCollectionRows(context.collections.portfolioInstances)
    : [];
  const strategyInstanceIds = portfolioInstanceRows.map(
    (portfolio) => portfolio.id,
  );
  const strategyInstanceIdSet = new Set(strategyInstanceIds);
  const hydratedSummaryIds = context.hydration.summaries;
  const hydratedOwnerPortfolioIds = context.hydration.ownerPortfolioIds;
  const hasHydratedOwnerPortfolioList =
    hydratedOwnerPortfolioIds.size > 0 &&
    Array.from(hydratedOwnerPortfolioIds).every(
      (strategyInstanceId) =>
        strategyInstanceIdSet.has(strategyInstanceId) &&
        hydratedSummaryIds.has(strategyInstanceId),
    );
  const hasHydratedVisibleSummaries =
    strategyInstanceIds.length > 0 &&
    strategyInstanceIds.every((strategyInstanceId) =>
      hydratedSummaryIds.has(strategyInstanceId),
    );

  return {
    hasHydratedSummaries:
      hydratedOwnerPortfolioIds.size === 0
        ? hasHydratedVisibleSummaries
        : hasHydratedOwnerPortfolioList,
    hasPortfolioInstances: portfolioInstanceRows.length > 0,
    hydratedStrategyInstanceIds: strategyInstanceIds.filter(
      (strategyInstanceId) => hydratedSummaryIds.has(strategyInstanceId),
    ),
    strategyInstanceIds,
  };
}

function resolvePortfolioBootstrapQueryClient(
  client: QueryClient | undefined,
): QueryClient {
  return (client ?? (queryClient as unknown as QueryClient)) as QueryClient;
}

function resolvePortfolioBootstrapPlatformApi(
  platformApi: PlatformApiTrpcClient | undefined,
): PlatformApiTrpcClient {
  return platformApi ?? getBrowserPlatformApiUnbatchedClient();
}

export function getPortfolioDetailBootstrapQueryOptions(
  input: PortfolioDetailBootstrapInput,
) {
  const normalizedPortfolioId = input.portfolioId.trim();
  const historyLimit = normalizePositiveInteger(input.historyLimit, 30);
  const sessionScopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const dependencies: PortfolioDbDependencies = {
    platformApi: resolvePortfolioBootstrapPlatformApi(input.platformApi),
    queryClient: resolvePortfolioBootstrapQueryClient(input.queryClient),
    sessionScopeToken,
  };

  return {
    queryKey: withSessionQueryScope(
      queryKeys.portfolio.detailBootstrapById(
        normalizedPortfolioId,
        historyLimit,
        input.performanceResolution ?? null,
        input.positionsInSharesUiEnabled === true,
      ),
      {
        scopeToken: sessionScopeToken,
      },
    ),
    queryFn: async () =>
      await queryPortfolioDetailNormalizedData(dependencies, {
        history: {
          includeEmptyRebalances: false,
          limit: historyLimit,
        },
        include: {
          assetPnl: true,
          assetPrices: true,
          assets: true,
          dashboardAssetSummaries: input.positionsInSharesUiEnabled === true,
          history: true,
          performancePoints: true,
          performanceSummary: true,
          portfolioInstances: true,
          positions: true,
          schedule: true,
          vaults: true,
        },
        performanceResolution: input.performanceResolution,
        returnMethod: PerformanceReturnMethod.MWR,
        strategyInstanceId: normalizedPortfolioId,
      }),
    retry: false,
    staleTime: PORTFOLIO_DETAIL_BOOTSTRAP_STALE_TIME_MS,
  } as const;
}

export async function ensurePortfolioDetailSeed(
  input: PortfolioDetailBootstrapInput,
): Promise<PortfolioDetailNormalizedResponse | null> {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return null;
  }

  const client = resolvePortfolioBootstrapQueryClient(input.queryClient);
  const dependencies = getPortfolioDependencies({
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });
  const [detailPayload, performanceSeriesPayload, scheduledFunctionsPayload] =
    await Promise.all([
      client.ensureQueryData(
        getPortfolioDetailBootstrapQueryOptions({
          ...input,
          portfolioId: normalizedPortfolioId,
          queryClient: client,
        }),
      ),
      queryPortfolioPerformanceSeriesData(
        dependencies,
        normalizedPortfolioId,
      ).catch(() =>
        input.clearPerformanceLivePositionsOnError
          ? emptyPortfolioPerformanceSeriesResponse(normalizedPortfolioId)
          : null,
      ),
      queryPortfolioScheduledFunctionsData(
        dependencies,
        normalizedPortfolioId,
      ).catch(() => null),
    ]);
  const collections = getPortfolioDomainCollections({
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });

  applyDashboardPayloadToCollections(
    collections,
    {
      assetPrices: detailPayload.assetPrices,
      assets: detailPayload.assets,
      dashboardAssetSummaries: detailPayload.dashboardAssetSummaries,
      positions: detailPayload.positions,
      portfolioInstances: detailPayload.portfolioInstances,
      vaults: detailPayload.vaults,
    },
    new Set(),
  );
  applyDetailPayloadToCollections(
    collections,
    normalizedPortfolioId,
    detailPayload,
    {
      includeAssetPnl: true,
      includeAssetUniverse: true,
      includePerformanceSummary: true,
    },
  );
  applyPerformanceSeriesPayloadToCollections(
    collections,
    normalizedPortfolioId,
    performanceSeriesPayload,
  );
  applyPortfolioScheduledFunctionsPayloadToCollections(
    collections,
    normalizedPortfolioId,
    scheduledFunctionsPayload,
  );
  if (performanceSeriesPayload !== null) {
    markPortfolioDetailsHydrated(
      {
        hasPerformanceSeriesRows: performanceSeriesPayload.series.length > 0,
        portfolioId: normalizedPortfolioId,
        positionsInSharesUiEnabled: input.positionsInSharesUiEnabled === true,
      },
      {
        ownerAddresses: input.ownerAddresses,
        sessionScopeToken: input.sessionScopeToken,
      },
    );
  }
  markPortfolioSummariesHydrated([normalizedPortfolioId], {
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });

  return detailPayload;
}

export async function invalidatePortfolioDetailBootstrapQuery(
  input: Omit<PortfolioDetailBootstrapInput, "platformApi">,
): Promise<void> {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return;
  }

  const client = resolvePortfolioBootstrapQueryClient(input.queryClient);
  await client.invalidateQueries({
    queryKey: getPortfolioDetailBootstrapQueryOptions({
      ...input,
      portfolioId: normalizedPortfolioId,
      queryClient: client,
    }).queryKey,
  });
}

export function hasPortfolioDetailSeeded(
  input: Omit<PortfolioDetailBootstrapInput, "platformApi">,
): boolean {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return false;
  }

  // "Seeded" means a detail bootstrap path explicitly finished and marked this
  // portfolio as hydrated for the current viewer scope. We intentionally do not
  // infer readiness from collection row presence alone, because partial row
  // updates can exist before the performance-series payload is actually ready.
  return readExistingPortfolioGraph(
    input,
    (context) =>
      context?.hydration.details.get(normalizedPortfolioId)
        ?.positionsInSharesUiEnabled ===
      (input.positionsInSharesUiEnabled === true),
  );
}

export function hasHydratedPortfolioPerformanceSeriesRows(
  input: Omit<PortfolioDetailBootstrapInput, "platformApi">,
): boolean {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return false;
  }

  return readExistingPortfolioGraph(
    input,
    (context) =>
      context?.hydration.details.get(normalizedPortfolioId)
        ?.hasPerformanceSeriesRows ?? false,
  );
}

export {
  buildPortfolioDetailViewQuery,
  normalizePortfolioNormalizedContractError,
};
export type { PortfolioDomainCollections };

export function preloadPortfolioDetailView(
  input: PortfolioDetailBootstrapInput,
): Promise<void> {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return Promise.resolve();
  }

  const collections = getPortfolioDomainCollections({
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: input.sessionScopeToken,
  });

  return ensurePortfolioDetailSeed({
    ...input,
    portfolioId: normalizedPortfolioId,
  })
    .then(async () => {
      await queryOnce(
        buildPortfolioDetailViewQuery({
          collections,
          historyLimit: input.historyLimit,
          portfolioId: normalizedPortfolioId,
        }),
      );
    })
    .catch(() => undefined);
}

export async function hydratePortfolioIndex(
  ownerAddresses: string[],
  options?: {
    sessionScopeToken?: string;
  },
): Promise<string[]> {
  const normalizedOwnerAddresses = uniqueNormalizedAddresses(ownerAddresses);
  const hydrationCacheKey = toPortfolioIndexHydrationCacheKey({
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const graphOptions = {
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  };
  const collections = getPortfolioDomainCollections(graphOptions);
  const existingPromise = readExistingPortfolioGraph(graphOptions, (context) =>
    context?.hydration.indexHydrations.get(hydrationCacheKey),
  );
  if (existingPromise) {
    return await existingPromise;
  }

  const hydrationPromise = (async () => {
    const dependencies = getPortfolioDependencies({
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    });
    const principalOwnerAddressSet = new Set(normalizedOwnerAddresses);

    const payload = await queryDashboardNormalizedData(dependencies, {
      archiveStatus: "all",
      include: {
        assetPrices: false,
        assets: false,
        dashboardAssetSummaries: false,
        portfolioAssetBalances: false,
        portfolioInstances: true,
        positions: false,
        vaults: false,
      },
      limit: 250,
    });

    applyDashboardPayloadToCollections(
      collections,
      payload,
      principalOwnerAddressSet,
    );
    const strategyInstanceIds = extractPortfolioIdsFromPayload(payload);
    markOwnerPortfolioListHydrated(strategyInstanceIds, {
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    });

    return strategyInstanceIds;
  })();

  readExistingPortfolioGraph(graphOptions, (context) =>
    context?.hydration.indexHydrations.set(hydrationCacheKey, hydrationPromise),
  );

  try {
    return await hydrationPromise;
  } finally {
    readExistingPortfolioGraph(graphOptions, (context) => {
      if (
        context?.hydration.indexHydrations.get(hydrationCacheKey) ===
        hydrationPromise
      ) {
        context.hydration.indexHydrations.delete(hydrationCacheKey);
      }
    });
  }
}

export async function hydratePortfolioDashboardSeed(
  ownerAddresses: string[],
  options?: {
    sessionScopeToken?: string;
  },
): Promise<string[]> {
  const normalizedOwnerAddresses = uniqueNormalizedAddresses(ownerAddresses);
  const hydrationCacheKey = toPortfolioDashboardSeedHydrationCacheKey({
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const graphOptions = {
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  };
  getPortfolioDomainCollections(graphOptions);

  const cachedSeed = readPortfolioDashboardSeedCache(
    hydrationCacheKey,
    graphOptions,
  );
  if (cachedSeed) {
    const cachedStrategyInstanceIds = applyPortfolioDashboardSeedPayload({
      deleteStalePortfolioRows: false,
      ownerAddresses: normalizedOwnerAddresses,
      payload: cachedSeed.payload,
      sessionScopeToken: options?.sessionScopeToken,
    });

    const hasRefreshInFlight = readExistingPortfolioGraph(
      graphOptions,
      (context) =>
        context?.hydration.summaryHydrations.has(hydrationCacheKey) ?? false,
    );
    if (!hasRefreshInFlight) {
      const refreshPromise = hydratePortfolioDashboardSeedUncached({
        hydrationCacheKey,
        ownerAddresses: normalizedOwnerAddresses,
        sessionScopeToken: options?.sessionScopeToken,
      });
      void refreshPromise.catch(() => undefined);
    }

    return cachedStrategyInstanceIds.length > 0
      ? cachedStrategyInstanceIds
      : cachedSeed.strategyInstanceIds;
  }

  const existingPromise = readExistingPortfolioGraph(graphOptions, (context) =>
    context?.hydration.summaryHydrations.get(hydrationCacheKey),
  );
  if (existingPromise) {
    await existingPromise;
    return getPortfolioDomainSeedSnapshot({
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    }).strategyInstanceIds;
  }

  return await hydratePortfolioDashboardSeedUncached({
    hydrationCacheKey,
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
}

async function hydratePortfolioDashboardSeedUncached(input: {
  hydrationCacheKey: string;
  ownerAddresses: string[];
  sessionScopeToken?: string;
}): Promise<string[]> {
  let hydratedStrategyInstanceIds: string[] = [];
  const hydrationPromise = (async () => {
    const dependencies = getPortfolioDependencies({
      ownerAddresses: input.ownerAddresses,
      sessionScopeToken: input.sessionScopeToken,
    });
    const payload = await queryDashboardNormalizedData(dependencies, {
      archiveStatus: "all",
      include: {
        assetPrices: true,
        assets: true,
        dashboardAssetSummaries: true,
        performanceSeries: false,
        portfolioAssetBalances: false,
        portfolioInstances: true,
        positions: true,
        vaults: true,
      },
      includeInvestingAccount: true,
      limit: 250,
    });

    hydratedStrategyInstanceIds = applyPortfolioDashboardSeedPayload({
      ownerAddresses: input.ownerAddresses,
      payload,
      sessionScopeToken: input.sessionScopeToken,
    });
    writePortfolioDashboardSeedCache(
      input.hydrationCacheKey,
      payload,
      hydratedStrategyInstanceIds,
      input,
    );
  })();

  readExistingPortfolioGraph(input, (context) =>
    context?.hydration.summaryHydrations.set(
      input.hydrationCacheKey,
      hydrationPromise,
    ),
  );

  try {
    await hydrationPromise;
    return hydratedStrategyInstanceIds;
  } finally {
    readExistingPortfolioGraph(input, (context) => {
      if (
        context?.hydration.summaryHydrations.get(input.hydrationCacheKey) ===
        hydrationPromise
      ) {
        context.hydration.summaryHydrations.delete(input.hydrationCacheKey);
      }
    });
  }
}

// Note: collections remain `syncMode: "on-demand"` and live queries can fetch
// subsets lazily. We still keep these imperative hydrators because the backend
// already returns normalized multi-entity payloads. Seeding multiple
// collections from one response avoids fan-out into several narrower detail
// queries and gives the UI explicit refresh/polling control where we need it.
export async function hydratePortfolioSummaries(
  strategyInstanceIds: string[],
  options?: PortfolioSummaryHydrationOptions,
): Promise<void> {
  const normalizedStrategyInstanceIds = uniqueNonEmpty(strategyInstanceIds);
  if (normalizedStrategyInstanceIds.length === 0) {
    return;
  }

  const include = normalizePortfolioSummaryHydrationInclude(options);
  if (
    !include.assetPrices &&
    !include.assets &&
    !include.dashboardAssetSummaries &&
    !include.performance &&
    !include.portfolioInstances &&
    !include.positions &&
    !include.vaults
  ) {
    return;
  }

  const hydrationCacheKey = toPortfolioSummaryHydrationCacheKey({
    include,
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
    strategyInstanceIds: normalizedStrategyInstanceIds,
  });
  const graphOptions = {
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  };
  getPortfolioDomainCollections(graphOptions);
  const existingPromise = readExistingPortfolioGraph(graphOptions, (context) =>
    context?.hydration.summaryHydrations.get(hydrationCacheKey),
  );
  if (existingPromise) {
    await existingPromise;
    return;
  }

  const hydrationPromise = (async () => {
    const normalizedOwnerAddresses = uniqueNormalizedAddresses(
      options?.ownerAddresses ?? [],
    );
    const collections = getPortfolioDomainCollections({
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    });
    const dependencies = getPortfolioDependencies({
      ownerAddresses: normalizedOwnerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    });
    const principalOwnerAddressSet = new Set(normalizedOwnerAddresses);
    const shouldFetchDashboardPayload =
      include.assetPrices ||
      include.assets ||
      include.dashboardAssetSummaries ||
      include.performance ||
      include.portfolioInstances ||
      include.positions ||
      include.vaults;
    const responses = shouldFetchDashboardPayload
      ? await Promise.all(
          chunkArray(normalizedStrategyInstanceIds, STRATEGY_BATCH_LIMIT).map(
            (chunk) =>
              queryDashboardNormalizedData(dependencies, {
                archiveStatus: "all",
                include: {
                  assetPrices: include.assetPrices,
                  assets: include.assets,
                  dashboardAssetSummaries: include.dashboardAssetSummaries,
                  performanceSeries: include.performance,
                  portfolioAssetBalances: false,
                  portfolioInstances: include.portfolioInstances,
                  positions: include.positions,
                  vaults: include.vaults,
                },
                performanceSeries: include.performance
                  ? {
                      maxPointsPerPortfolio:
                        include.performanceMaxPointsPerPortfolio,
                      resolution: include.performanceResolution,
                    }
                  : undefined,
                strategyInstanceIds: chunk,
              }),
          ),
        )
      : [];

    const merged = mergeNormalizedPayloads(responses);
    deleteStalePortfolioBoundSummaryRows({
      collections,
      include,
      payload: merged,
      portfolioIds: new Set(normalizedStrategyInstanceIds),
    });
    applyDashboardPayloadToCollections(
      collections,
      merged,
      principalOwnerAddressSet,
    );

    const hydratedSummaryComponents =
      getPortfolioSummaryHydrationComponents(include);

    // Dashboard sparkline refreshes are performance-only. Do not let those mark
    // positions/vaults as hydrated, or the summary live queries can short-circuit
    // to empty relation rows after a route change.
    markPortfolioSummaryComponentsHydrated(
      normalizedStrategyInstanceIds,
      hydratedSummaryComponents,
      {
        ownerAddresses: normalizedOwnerAddresses,
        sessionScopeToken: options?.sessionScopeToken,
      },
    );

    if (
      include.assetPrices ||
      include.assets ||
      include.dashboardAssetSummaries ||
      include.portfolioInstances ||
      include.positions ||
      include.vaults
    ) {
      markPortfolioSummariesHydrated(normalizedStrategyInstanceIds, {
        ownerAddresses: normalizedOwnerAddresses,
        sessionScopeToken: options?.sessionScopeToken,
      });
    }
  })();

  readExistingPortfolioGraph(graphOptions, (context) =>
    context?.hydration.summaryHydrations.set(
      hydrationCacheKey,
      hydrationPromise,
    ),
  );

  try {
    await hydrationPromise;
  } finally {
    readExistingPortfolioGraph(graphOptions, (context) => {
      if (
        context?.hydration.summaryHydrations.get(hydrationCacheKey) ===
        hydrationPromise
      ) {
        context.hydration.summaryHydrations.delete(hydrationCacheKey);
      }
    });
  }
}

function getAccountPerformanceSeriesPortfolioAddress(
  queryKey: readonly unknown[],
): string {
  const accountAddressSegment =
    queryKey[queryKeys.dashboard.accountPerformanceSeries.length];
  return typeof accountAddressSegment === "string"
    ? normalizePortfolioAddress(accountAddressSegment)
    : "";
}

async function invalidateAccountPerformanceSeries(input: {
  accountAddress?: string | null;
  ownerAddresses: string[];
  queryClient?: QueryClient;
}): Promise<void> {
  const accountAddress = normalizePortfolioAddress(
    input.accountAddress ?? input.ownerAddresses[0] ?? "",
  );
  if (accountAddress.length === 0) {
    return;
  }

  await (input.queryClient ?? queryClient).invalidateQueries({
    predicate: (query) =>
      matchesQueryKeyPrefix(
        query.queryKey,
        queryKeys.dashboard.accountPerformanceSeries,
      ) &&
      getAccountPerformanceSeriesPortfolioAddress(query.queryKey) ===
        accountAddress,
  });
}

export async function refreshPortfolioDomainAfterPortfolioCreate(input: {
  accountAddress?: string | null;
  ownerAddresses: string[];
  portfolioId: string;
  queryClient?: QueryClient;
  sessionScopeToken?: string;
}): Promise<void> {
  const portfolioId = input.portfolioId.trim();
  const ownerAddresses = uniqueNormalizedAddresses(input.ownerAddresses);
  if (portfolioId.length === 0 || ownerAddresses.length === 0) {
    return;
  }

  // Creation is a known mutation point. Do targeted discovery/hydration instead
  // of resetting the owner-scoped TanStack DB collections and flashing empty UI.
  await hydratePortfolioIndex(ownerAddresses, {
    sessionScopeToken: input.sessionScopeToken,
  });
  await Promise.all([
    hydratePortfolioSummaries([portfolioId], {
      includeDashboardAssetSummaries: true,
      includePerformance: true,
      ownerAddresses,
      sessionScopeToken: input.sessionScopeToken,
    }),
    invalidateAccountPerformanceSeries({
      accountAddress: input.accountAddress,
      ownerAddresses,
      queryClient: input.queryClient,
    }),
  ]);
}

export async function hydratePortfolioDetail(
  portfolioId: string,
  options?: {
    includeAssetPnl?: boolean;
    includeAssetPrices?: boolean;
    includeAssets?: boolean;
    includeHistory?: boolean;
    ownerAddresses?: string[];
    includePortfolioInstances?: boolean;
    includePositions?: boolean;
    includePerformance?: boolean;
    includeSchedule?: boolean;
    includeVaults?: boolean;
    historyLimit?: number;
    performanceResolution?: PortfolioDetailNormalizedInput["performanceResolution"];
    positionsInSharesUiEnabled?: boolean;
    sessionScopeToken?: string;
  },
): Promise<PortfolioDetailNormalizedResponse | null> {
  const normalizedPortfolioId = portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return null;
  }

  const collections = getPortfolioDomainCollections({
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const dependencies = getPortfolioDependencies({
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });

  const detailPayload = await queryPortfolioDetailNormalizedData(dependencies, {
    history: {
      includeEmptyRebalances: false,
      limit: options?.historyLimit ?? 30,
    },
    include: {
      assetPnl: options?.includeAssetPnl ?? false,
      assetPrices: options?.includeAssetPrices ?? true,
      assets: options?.includeAssets ?? true,
      dashboardAssetSummaries:
        (options?.includePositions ?? true) &&
        options?.positionsInSharesUiEnabled === true,
      history: options?.includeHistory ?? true,
      performancePoints: options?.includePerformance ?? true,
      performanceSummary: options?.includePerformance ?? true,
      portfolioInstances: options?.includePortfolioInstances ?? true,
      positions: options?.includePositions ?? true,
      schedule: options?.includeSchedule ?? true,
      vaults: options?.includeVaults ?? true,
    },
    performanceResolution: options?.performanceResolution,
    returnMethod: PerformanceReturnMethod.MWR,
    strategyInstanceId: normalizedPortfolioId,
  });
  const performanceSeriesPayload =
    (options?.includePerformance ?? true)
      ? await queryPortfolioPerformanceSeriesData(
          dependencies,
          normalizedPortfolioId,
        ).catch(() => null)
      : null;
  const scheduledFunctionsPayload =
    (options?.includeSchedule ?? true)
      ? await queryPortfolioScheduledFunctionsData(
          dependencies,
          normalizedPortfolioId,
        ).catch(() => null)
      : null;
  const include = {
    assetPrices: options?.includeAssetPrices ?? true,
    assets: options?.includeAssets ?? true,
    dashboardAssetSummaries:
      (options?.includePositions ?? true) &&
      options?.positionsInSharesUiEnabled === true,
    portfolioInstances: options?.includePortfolioInstances ?? true,
    positions: options?.includePositions ?? true,
    vaults: options?.includeVaults ?? true,
  };

  deleteStalePortfolioBoundSummaryRows({
    collections,
    include,
    payload: {
      dashboardAssetSummaries: include.dashboardAssetSummaries
        ? detailPayload.dashboardAssetSummaries
        : [],
      positions: include.positions ? detailPayload.positions : [],
      vaults: include.vaults ? detailPayload.vaults : [],
    },
    portfolioIds: new Set([normalizedPortfolioId]),
  });
  applyDashboardPayloadToCollections(
    collections,
    {
      assetPrices: include.assetPrices ? detailPayload.assetPrices : [],
      assets: include.assets ? detailPayload.assets : [],
      dashboardAssetSummaries: include.dashboardAssetSummaries
        ? detailPayload.dashboardAssetSummaries
        : [],
      positions: include.positions ? detailPayload.positions : [],
      portfolioInstances: include.portfolioInstances
        ? detailPayload.portfolioInstances
        : [],
      vaults: include.vaults ? detailPayload.vaults : [],
    },
    new Set(),
  );
  applyDetailPayloadToCollections(
    collections,
    normalizedPortfolioId,
    detailPayload,
    {
      includeAssetPnl: options?.includeAssetPnl ?? false,
      includeAssetUniverse: include.assets,
      includePerformanceSummary: options?.includePerformance ?? true,
    },
  );
  applyPerformanceSeriesPayloadToCollections(
    collections,
    normalizedPortfolioId,
    performanceSeriesPayload,
  );
  applyPortfolioScheduledFunctionsPayloadToCollections(
    collections,
    normalizedPortfolioId,
    scheduledFunctionsPayload,
  );
  if (
    (options?.includePerformance ?? true) &&
    performanceSeriesPayload !== null
  ) {
    markPortfolioDetailsHydrated(
      {
        hasPerformanceSeriesRows: performanceSeriesPayload.series.length > 0,
        portfolioId: normalizedPortfolioId,
        positionsInSharesUiEnabled:
          options?.positionsInSharesUiEnabled === true,
      },
      {
        ownerAddresses: options?.ownerAddresses,
        sessionScopeToken: options?.sessionScopeToken,
      },
    );
  }
  markPortfolioSummariesHydrated([normalizedPortfolioId], {
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const hydratedDetailSummaryComponents =
    getPortfolioSummaryHydrationComponents({
      ...include,
      performance: false,
      portfolioInstances: include.portfolioInstances,
    });
  markPortfolioSummaryComponentsHydrated(
    [normalizedPortfolioId],
    hydratedDetailSummaryComponents,
    {
      ownerAddresses: options?.ownerAddresses,
      sessionScopeToken: options?.sessionScopeToken,
    },
  );
  return detailPayload;
}

export async function invalidatePortfolioDomainSummaries(options?: {
  sessionScopeToken?: string;
}): Promise<void> {
  clearPortfolioSummaryHydrationForSession(options?.sessionScopeToken);

  await invalidatePortfolioDomainCollectionQueries(
    PORTFOLIO_SUMMARY_COLLECTION_QUERY_KEYS,
    options?.sessionScopeToken,
  );
}

export async function invalidatePortfolioDomainDetail(input: {
  portfolioId: string;
  sessionScopeToken?: string;
}): Promise<void> {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return;
  }

  clearPortfolioSummaryHydrationForPortfolio({
    portfolioId: normalizedPortfolioId,
    sessionScopeToken: input.sessionScopeToken,
  });

  await invalidatePortfolioDomainDetailQueries({
    portfolioId: normalizedPortfolioId,
    queryKeysToInvalidate: PORTFOLIO_DETAIL_PORTFOLIO_BOUND_QUERY_KEYS,
    sessionScopeToken: input.sessionScopeToken,
  });
}

export async function invalidatePortfolioAllocationHistoryChart(input: {
  portfolioId: string;
  sessionScopeToken?: string;
}): Promise<void> {
  const normalizedPortfolioId = input.portfolioId.trim();
  if (normalizedPortfolioId.length === 0) {
    return;
  }

  await invalidatePortfolioDomainDetailQueries({
    portfolioId: normalizedPortfolioId,
    queryKeysToInvalidate: [queryKeys.portfolio.allocationHistoryCheckpoints],
    sessionScopeToken: input.sessionScopeToken,
  });
}

export async function refreshAssetPricesForAssetIds(
  assetIds: string[],
  options?: {
    ownerAddresses?: string[];
    sessionScopeToken?: string;
  },
): Promise<void> {
  const normalizedAssetIdCandidates: string[] = [];
  for (const assetId of assetIds) {
    const normalizedAssetId = tryAssetIdLikeToLegacyAssetId(assetId);
    if (normalizedAssetId) {
      normalizedAssetIdCandidates.push(normalizedAssetId);
    }
  }
  const normalizedAssetIds = uniqueNonEmpty(normalizedAssetIdCandidates);
  if (normalizedAssetIds.length === 0) {
    return;
  }

  const collections = getPortfolioDomainCollections({
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const dependencies = getPortfolioDependencies({
    ownerAddresses: options?.ownerAddresses,
    sessionScopeToken: options?.sessionScopeToken,
  });

  const responses = await Promise.all(
    chunkArray(normalizedAssetIds, ASSET_BATCH_LIMIT).map((assetIdsChunk) =>
      queryDashboardNormalizedData(dependencies, {
        assetIds: assetIdsChunk,
        include: {
          assetPrices: true,
          assets: false,
          dashboardAssetSummaries: false,
          portfolioAssetBalances: false,
          portfolioInstances: false,
          positions: false,
          vaults: false,
        },
      }),
    ),
  );

  const merged = mergeNormalizedPayloads(responses);
  const mergedAssetPrices = merged.assetPrices ?? [];
  if (mergedAssetPrices.length === 0) {
    return;
  }

  collections.assetPrices.utils.writeUpsert(mergedAssetPrices);
}

export function resetPortfolioDomainCollectionsCache(
  sessionScopeToken?: string,
) {
  if (!sessionScopeToken) {
    portfolioGraphStore.reset();
    resetBrowserPlatformApiClient();
    return;
  }

  portfolioGraphStore.reset({ sessionScopeToken });
}

export function resetPortfolioDomainCollectionsForTests() {
  resetPortfolioDomainCollectionsCache();
}
